import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MapComponent, MarkerComponent, PopupComponent } from '@maplibre/ngx-maplibre-gl';
import { LngLatBoundsLike } from 'maplibre-gl';
// MessageService stays: it is the toast plumbing the whole gateway domain publishes to.
import { MessageService } from 'primeng/api';
import {
  IntegratorSelectComponent,
  IntegratorSelectOption,
  WhButtonComponent,
  WhChipComponent,
  WhSegmentedComponent,
  WhSegmentedOption,
} from '@wheere-front/shared-ui';
import { GatewayRtkConfig, RtkBase, RtkMode, UpdateGatewayRtkRequest } from '@wheere/api-gateway';
import { mapStyleMap } from '@wheere-front/shared-map-libre';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { GatewaysV7Store } from '../data/gateways-v7.store';

/**
 * V7 Gateway RTK component — recreated from inside `GatewayRtkComponent` for the integrator.
 *
 * <p>Shows a MapLibre map centered on the gateway with all known Centipede bases, plus a
 * Mode toggle (AUTO / MANUAL) and a base selector enabled in MANUAL. Saving POSTs to
 * {@code PUT /gateways/{id}/rtk} via {@link GatewaysV7Store#updateGatewayRtk}.
 *
 * <p>The map style key is read from {@link MAPTILES_KEY} provided in the integrator app
 * config — same MapTiler key as inside.
 */
@Component({
  selector: 'lib-gateway-rtk-map',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    IntegratorSelectComponent,
    WhButtonComponent,
    WhChipComponent,
    WhSegmentedComponent,
    MapComponent,
    MarkerComponent,
    PopupComponent,
    TranslocoModule,
  ],
  template: `
    <div class="flex h-full flex-col gap-4">
      <!-- Toolbar. Every control sits at h-8 so the row reads as one strip. -->
      <div
        class="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800">
        <!-- Mode / base / Save : PATCH /gateways/{id}/rtk exige gateway:write (ADR-025 GW4). En lecture
             seule il ne reste que la base actuellement assignée. -->
        @if (!readOnly()) {
          <span class="text-xs font-medium text-gray-500 dark:text-slate-400">{{
            'gatewayRtkMap.toolbar.mode' | transloco
          }}</span>
          <wh-segmented
            size="sm"
            [options]="modeOptions()"
            [value]="selectedMode()"
            [scroll]="false"
            (valueChange)="onModeChange($event)" />

          @if (selectedMode() === 'MANUAL') {
            <lib-integrator-select
              width="20rem"
              size="sm"
              accent="warmYellow"
              [placeholder]="'gatewayRtkMap.toolbar.selectBase' | transloco"
              [filterable]="true"
              [options]="baseOptions()"
              [ngModel]="selectedBaseId()"
              (ngModelChange)="selectedBaseId.set($event)" />
          }

          <wh-button variant="warmYellow" size="xs" [disabled]="!canSave()" [loading]="saving()" (click)="onSave()">
            {{ 'gatewayRtkMap.toolbar.save' | transloco }}
          </wh-button>
        }

        @if (currentBase(); as base) {
          <span class="ml-auto flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
            <span>{{ 'gatewayRtkMap.toolbar.currentlyAssigned' | transloco: { mode: effectiveMode() } }}</span>
            <span class="wh-display font-semibold text-gray-900 dark:text-slate-100">{{ base.baseId }}</span>
            <span>{{ formatDistance(base.distanceKm) }}</span>
            <wh-chip
              [label]="(base.active ? 'gatewayRtkMap.status.active' : 'gatewayRtkMap.status.inactive') | transloco"
              [tone]="base.active ? 'ok' : 'error'" />
          </span>
        } @else if (readOnly()) {
          <span class="text-xs text-gray-500 dark:text-slate-400"
            >{{ 'gatewayRtkMap.toolbar.mode' | transloco }} {{ effectiveMode() }}</span
          >
        }
      </div>

      <!-- Map — height fills the available container (parent provides the bound) -->
      @if (mapCenter(); as center) {
        <div
          class="relative min-h-[320px] flex-1 overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700">
          <mgl-map
            [mapStyle]="mapStyleStreet()"
            [center]="center"
            [zoom]="[mapZoom()]"
            [fitBounds]="mapBounds() ?? undefined"
            [fitBoundsOptions]="{ padding: 60, maxZoom: 11 }"
            [interactive]="true">
            @if (gatewayPosition(); as gw) {
              <mgl-marker [lngLat]="[gw.lon, gw.lat]" [anchor]="'center'">
                <div class="gw-marker" [title]="'gatewayRtkMap.markers.gatewayPosition' | transloco"></div>
              </mgl-marker>
            }

            @for (base of bases(); track base.baseId) {
              <mgl-marker [lngLat]="[+base.lon, +base.lat]" [anchor]="'center'">
                <div
                  class="base-marker"
                  [class.base-active]="base.active"
                  [class.base-inactive]="!base.active"
                  [class.base-current]="base.baseId === currentBase()?.baseId"
                  [title]="
                    base.baseId +
                    ' (' +
                    ((base.active ? 'gatewayRtkMap.status.active' : 'gatewayRtkMap.status.inactive') | transloco) +
                    ')'
                  "
                  (click)="popupBase.set(base)"></div>
              </mgl-marker>
            }

            @if (popupBase(); as base) {
              <mgl-popup
                [lngLat]="[+base.lon, +base.lat]"
                [closeButton]="true"
                [offset]="14"
                (popupClose)="popupBase.set(null)">
                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-2">
                    <span class="wh-display font-semibold">{{ base.baseId }}</span>
                    <wh-chip
                      [label]="
                        (base.active ? 'gatewayRtkMap.status.active' : 'gatewayRtkMap.status.inactive') | transloco
                      "
                      [tone]="base.active ? 'ok' : 'error'" />
                  </div>
                  @if (base.distanceKm !== null && base.distanceKm !== undefined) {
                    <span class="text-xs text-gray-500">{{
                      'gatewayRtkMap.popup.distance' | transloco: { value: formatDistance(base.distanceKm) }
                    }}</span>
                  }
                  <span class="wh-display text-xs text-gray-500">{{ base.lat }}, {{ base.lon }}</span>
                </div>
              </mgl-popup>
            }
          </mgl-map>

          <div class="rtk-legend">
            <span class="legend-item"
              ><span class="dot dot-gw"></span> {{ 'gatewayRtkMap.legend.gateway' | transloco }}</span
            >
            <span class="legend-item"
              ><span class="dot dot-active"></span> {{ 'gatewayRtkMap.legend.activeBase' | transloco }}</span
            >
            <span class="legend-item"
              ><span class="dot dot-inactive"></span> {{ 'gatewayRtkMap.legend.inactiveBase' | transloco }}</span
            >
            <span class="legend-item"
              ><span class="dot dot-current"></span> {{ 'gatewayRtkMap.legend.currentlyAssigned' | transloco }}</span
            >
          </div>
        </div>
      } @else {
        <div
          class="flex h-80 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-sm text-gray-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-500">
          {{ 'gatewayRtkMap.noData' | transloco }}
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }

      :host ::ng-deep mgl-map,
      mgl-map {
        display: block;
        width: 100%;
        height: 100%;
      }
      .gw-marker {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #2563eb;
        border: 3px solid #fff;
        box-shadow:
          0 0 0 2px #2563eb,
          0 2px 6px rgba(0, 0, 0, 0.4);
      }
      .base-marker {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
        cursor: pointer;
        transition: transform 0.1s ease;
      }
      .base-marker:hover {
        transform: scale(1.4);
      }
      .base-active {
        background: #22c55e;
      }
      .base-inactive {
        background: #ef4444;
      }
      .base-current {
        outline: 3px solid #f59e0b;
        outline-offset: 1px;
      }
      .rtk-legend {
        position: absolute;
        bottom: 8px;
        left: 8px;
        display: flex;
        gap: 12px;
        padding: 6px 10px;
        background: rgba(255, 255, 255, 0.92);
        color: #111827;
        border-radius: 6px;
        font-size: 11px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        z-index: 1;
      }
      /* The legend sits on the map, so it carries its own surface per theme. */
      :host-context(.dark) .rtk-legend {
        background: rgba(15, 23, 42, 0.92);
        color: #e2e8f0;
      }
      .legend-item {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
      .dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
      }
      .dot-gw {
        background: #2563eb;
      }
      .dot-active {
        background: #22c55e;
      }
      .dot-inactive {
        background: #ef4444;
      }
      .dot-current {
        background: #22c55e;
        outline: 2px solid #f59e0b;
        outline-offset: 1px;
      }
    `,
  ],
})
export class GatewayRtkMapComponent implements OnInit {
  gatewayId = input.required<string>();
  /** MapTiler API key — passed in by the parent so we don't depend on the integrator app token. */
  maptilesKey = input<string>('');

  /** Lecture seule (pas de gateway:write) : ni changement de mode, ni de base, ni sauvegarde. */
  readOnly = input<boolean>(false);

  #transloco = inject(TranslocoService);

  private readonly activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });

  protected onModeChange(mode: RtkMode | undefined) {
    if (mode) this.selectedMode.set(mode);
  }

  /** The shared select renders a plain label, so the distance and active flag the old
   * rich item template showed are folded into the text — it stays filterable. */
  protected readonly baseOptions = computed<IntegratorSelectOption<string>[]>(() => {
    this.activeLang();
    return this.bases().map(b => ({
      label: `${b.baseId} · ${this.formatDistance(b.distanceKm)} · ${this.#transloco.translate(
        b.active ? 'gatewayRtkMap.status.active' : 'gatewayRtkMap.status.inactive'
      )}`,
      value: b.baseId,
    }));
  });

  protected readonly modeOptions = computed<WhSegmentedOption<RtkMode>[]>(() => {
    // Re-derive labels when the active language changes.
    this.activeLang();
    return [
      { label: this.#transloco.translate('gatewayRtkMap.mode.auto'), value: 'AUTO' },
      { label: this.#transloco.translate('gatewayRtkMap.mode.manual'), value: 'MANUAL' },
    ];
  });

  protected readonly mapStyleStreet = computed(() => mapStyleMap(this.maptilesKey()).street);

  protected readonly config = signal<GatewayRtkConfig | null>(null);
  protected readonly bases = signal<RtkBase[]>([]);
  protected readonly saving = signal(false);
  protected readonly popupBase = signal<RtkBase | null>(null);

  protected readonly selectedMode = signal<RtkMode>('AUTO');
  protected readonly selectedBaseId = signal<string | null>(null);

  protected readonly currentBase = computed(() => this.config()?.base ?? null);
  protected readonly effectiveMode = computed(() => this.config()?.mode ?? 'AUTO');

  protected readonly gatewayPosition = computed(() => {
    const p = this.config()?.position;
    if (!p) return null;
    const lat = typeof p.lat === 'number' ? p.lat : Number(p.lat);
    const lon = typeof p.lon === 'number' ? p.lon : Number(p.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { lat, lon };
  });

  protected readonly mapCenter = computed<[number, number] | null>(() => {
    const gw = this.gatewayPosition();
    if (gw) return [gw.lon, gw.lat];
    const list = this.bases();
    if (!list.length) return null;
    const sample = list.slice(0, 5);
    const lon = sample.reduce((s, b) => s + Number(b.lon), 0) / sample.length;
    const lat = sample.reduce((s, b) => s + Number(b.lat), 0) / sample.length;
    return [lon, lat];
  });

  protected readonly mapZoom = signal(8);

  protected readonly mapBounds = computed<LngLatBoundsLike | null>(() => {
    const gw = this.gatewayPosition();
    const list = this.bases();
    if (!gw && !list.length) return null;
    const points: Array<[number, number]> = [];
    if (gw) points.push([gw.lon, gw.lat]);
    list.slice(0, 5).forEach(b => points.push([Number(b.lon), Number(b.lat)]));
    if (points.length < 2) return null;
    const lons = points.map(p => p[0]);
    const lats = points.map(p => p[1]);
    return [
      [Math.min(...lons), Math.min(...lats)],
      [Math.max(...lons), Math.max(...lats)],
    ];
  });

  protected readonly canSave = computed(() => {
    const cfg = this.config();
    if (!cfg) return false;
    if (this.saving()) return false;
    if (this.selectedMode() === 'MANUAL' && !this.selectedBaseId()) return false;
    const modeChanged = this.selectedMode() !== cfg.mode;
    const baseChanged = this.selectedBaseId() !== (cfg.baseId ?? null);
    return modeChanged || baseChanged;
  });

  #store = inject(GatewaysV7Store);
  #messageService = inject(MessageService);

  async ngOnInit(): Promise<void> {
    const id = this.gatewayId();
    const [config, bases] = await Promise.all([
      this.#store.loadRtk(id).then(() => this.#store.rtk()),
      this.#store.loadGatewayRtkBases(id).catch(() => [] as RtkBase[]),
    ]);
    if (config) this.applyConfig(config);
    this.bases.set(bases);
  }

  protected formatDistance(km: number | string | null | undefined): string {
    if (km == null) return '';
    const n = typeof km === 'number' ? km : Number(km);
    if (!Number.isFinite(n)) return '';
    return n < 1 ? `${Math.round(n * 1000)} m` : `${n.toFixed(1)} km`;
  }

  protected async onSave(): Promise<void> {
    if (this.readOnly()) return;
    this.saving.set(true);
    try {
      const request: UpdateGatewayRtkRequest = {
        mode: this.selectedMode(),
        baseId: this.selectedMode() === 'MANUAL' ? this.selectedBaseId() : null,
      };
      const updated = await this.#store.updateGatewayRtk(this.gatewayId(), request);
      this.applyConfig(updated);
      this.#messageService.add({
        severity: 'success',
        key: 'save',
        detail: this.#transloco.translate('gatewayRtkMap.toasts.rtkSet', {
          mode: updated.mode,
          base: updated.baseId ? ' / ' + updated.baseId : '',
        }),
      });
    } catch (e) {
      this.#messageService.add({
        severity: 'error',
        key: 'save',
        detail: this.#transloco.translate('gatewayRtkMap.toasts.updateFailed'),
      });
    } finally {
      this.saving.set(false);
    }
  }

  private applyConfig(cfg: GatewayRtkConfig): void {
    this.config.set(cfg);
    this.selectedMode.set(cfg.mode);
    this.selectedBaseId.set(cfg.baseId ?? null);
  }
}
